import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Bot,
  Brain,
  CheckCircle,
  Clock,
  FilePenLine,
  FileText,
  FileUp,
  Globe,
  HelpCircle,
  Link2,
  Loader2,
  MessageCircle,
  RefreshCw,
  Sparkles,
  PartyPopper,
  Search,
  TriangleAlert,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import WidgetPreview from "../../WidgetPreview";
import { useTranslation } from "../../../hooks/useTranslation";

const normalizeUrl = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Piano Free dell'onboarding: fino a 5 fonti totali (vedi scripts/seed-plans.js
// e apps/api/src/routes/sources.js). Oltre questo numero l'utente va avvisato
// di fare l'upgrade invece di lasciarlo scoprire un errore dal backend.

/** Freccia disegnata a mano accanto alla nota (asset del frame 4109:1129). */
const HandArrow = () => (
  <svg
    width="15"
    height="34"
    viewBox="0 0 14.6162 33.5002"
    fill="none"
    className="shrink-0 mt-3"
  >
    <path
      d="M13.866 0.750204C5.19938 3.7502 -7.93395 14.3502 8.86604 32.7502M1.36575 30.7502L8.86604 32.7502V24.7502"
      stroke="#64748B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Riga "intro" (più grande): identità del bot — nome, sottotitolo e messaggio
 * di benvenuto insieme, così si capisce tutto in un colpo d'occhio. */
const IntroRow = ({ name, subtitle, welcomeMessage }) => (
  <div className="flex items-start gap-2.5 px-1 py-3">
    <MessageCircle
      className="mt-0.5 h-5 w-5 shrink-0 text-[#020617]"
      strokeWidth={1.5}
    />
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <p className="truncate text-sm font-semibold tracking-[0.07px] text-[#020617]">
        {name}
      </p>
      {subtitle && (
        <p className="text-xs leading-4 tracking-[0.18px] text-[#64748B]">
          {subtitle}
        </p>
      )}
      {welcomeMessage && (
        <p className="text-xs leading-4 tracking-[0.18px] text-[#64748B]">
          {welcomeMessage}
        </p>
      )}
    </div>
  </div>
);

/** Riga fonte: icona colorata + titolo a sinistra, tipologia + cestino a destra. */
const SourceRow = ({ icon: Icon, iconClassName, title, kind, statusBadge, action }) => (
  <div className="flex items-center gap-2.5 px-1 py-2">
    <Icon className={`h-5 w-5 shrink-0 ${iconClassName}`} strokeWidth={1.5} />
    <p className="min-w-0 flex-1 truncate text-sm font-medium tracking-[0.07px] text-[#020617]">
      {title}
    </p>
    <span className="shrink-0 text-xs text-[#64748B]">{kind}</span>
    {statusBadge}
    {action}
  </div>
);

/** Input inline https:// + Aggiungi, riusato per aggiungere un link a mano. */
const UrlAdder = ({ label, placeholder, onAdd }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  const submit = () => {
    const url = normalizeUrl(value);
    if (url.length <= "https://".length + 3) return;
    onAdd(url);
    setValue("");
  };

  return (
    <div>
      <p className="mb-1 text-xs font-medium text-[#334155]">{label}</p>
      <div className="flex">
        <span className="flex items-center rounded-l-md border border-r-0 bg-muted px-2.5 text-xs text-muted-foreground">
          https://
        </span>
        <Input
          className="h-9 rounded-none text-sm"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
        />
        <Button
          type="button"
          variant="secondary"
          className="h-9 rounded-l-none border border-l-0"
          onClick={submit}
        >
          {t("onboardingWizard.add")}
        </Button>
      </div>
    </div>
  );
};

/**
 * Dialog "Analizza link": elenco dei link REALI trovati in header/nav/footer
 * del sito (già scoperti dall'analisi, non inventati), con selezione multipla
 * + aggiunta manuale di un link. Il limite di fonti del piano lo fa valere
 * chi aggiunge (il server, e la scelta del piano che si apre): qui niente
 * conti con un massimo fisso.
 */
const AnalyzeLinksDialog = ({
  open,
  onOpenChange,
  links,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(() => new Set());
  const [customLinks, setCustomLinks] = useState([]);
  const [search, setSearch] = useState("");

  // Ripartenza pulita ad ogni apertura: niente selezioni residue del giro
  // precedente (le fonti già aggiunte non tornano comunque in `links`).
  useEffect(() => {
    if (open) {
      setChecked(new Set());
      setCustomLinks([]);
      setSearch("");
    }
  }, [open]);

  const selectedCount = checked.size + customLinks.length;

  const query = search.trim().toLowerCase();
  const filteredLinks = query
    ? links.filter(
        (link) =>
          (link.name || "").toLowerCase().includes(query) ||
          link.url.toLowerCase().includes(query)
      )
    : links;

  const toggleLink = (url, isChecked) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (isChecked) next.add(url);
      else next.delete(url);
      return next;
    });
  };

  const addCustomLink = (url) => {
    if (
      links.some((l) => l.url === url) ||
      customLinks.some((l) => l.url === url)
    )
      return;
    setCustomLinks((prev) => [...prev, { url }]);
  };

  const removeCustomLink = (url) => {
    setCustomLinks((prev) => prev.filter((l) => l.url !== url));
  };

  const submit = () => {
    const chosen = [
      ...links
        .filter((l) => checked.has(l.url))
        .map((l) => ({ type: "url", url: l.url, title: l.name })),
      ...customLinks.map((l) => ({ type: "url", url: l.url })),
    ];
    if (chosen.length === 0) return;
    onConfirm(chosen);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("onboardingWizard.reviewD2LinkDialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("onboardingWizard.reviewD2LinkDialogDesc")}
          </DialogDescription>
        </DialogHeader>

        {links.length > 0 && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("onboardingWizard.reviewD2LinkDialogSearchPlaceholder")}
              className="h-9 pl-8 text-sm"
            />
          </div>
        )}

        <div className="max-h-[400px] space-y-1 overflow-y-auto rounded-md border border-black/10 p-1">
          {links.length === 0 && customLinks.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t("onboardingWizard.reviewD2LinkDialogEmpty")}
            </p>
          )}
          {links.length > 0 && filteredLinks.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t("onboardingWizard.reviewD2LinkDialogNoMatch")}
            </p>
          )}
          {filteredLinks.map((link) => {
            const isChecked = checked.has(link.url);
            return (
              <label
                key={link.url}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 hover:bg-muted/60"
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={(value) => toggleLink(link.url, !!value)}
                />
                <Globe className="h-4 w-4 shrink-0 text-sky-500" strokeWidth={1.5} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-[#020617]">
                    {link.name || link.url}
                  </span>
                  <span className="block truncate text-xs text-[#64748B]">
                    {link.url}
                  </span>
                </span>
              </label>
            );
          })}
          {customLinks.map((link) => (
            <div
              key={link.url}
              className="flex items-center gap-2.5 rounded-md bg-muted/40 px-2 py-2"
            >
              <Checkbox checked onCheckedChange={() => removeCustomLink(link.url)} />
              <Globe className="h-4 w-4 shrink-0 text-sky-500" strokeWidth={1.5} />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#020617]">
                {link.url}
              </span>
            </div>
          ))}
        </div>

        <UrlAdder
          label={t("onboardingWizard.reviewD2LinkDialogManualLabel")}
          placeholder="example.com/pagina"
          onAdd={addCustomLink}
        />

        <p className="text-xs text-[#64748B]">
          {t("onboardingWizard.reviewD2LinkDialogCount", { selected: selectedCount })}
        </p>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="button" onClick={submit} disabled={selectedCount === 0}>
            {t("onboardingWizard.reviewD2LinkDialogAdd", { count: selectedCount })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Dialog "Aggiungi testo": titolo + area di testo ampia, come l'aggiunta di
 * una fonte testuale dal CRM — qui in versione minimale per l'onboarding.
 */
const AddTextDialog = ({ open, onOpenChange, onConfirm }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    if (open) {
      setTitle("");
      setContent("");
    }
  }, [open]);

  const isValid = content.trim().length >= 10;

  const submit = () => {
    if (!isValid) return;
    onConfirm({
      title: title.trim() || t("onboardingWizard.reviewD2DetailsTitle"),
      content: content.trim(),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("onboardingWizard.reviewD2TextDialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("onboardingWizard.reviewD2TextDialogDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="review-text-title">
            {t("onboardingWizard.reviewD2TextDialogTitleLabel")}
          </Label>
          <Input
            id="review-text-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("onboardingWizard.reviewD2TextDialogTitlePlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="review-text-content">
            {t("onboardingWizard.reviewD2TextDialogContentLabel")}
          </Label>
          <textarea
            id="review-text-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t("onboardingWizard.reviewD2DetailsPlaceholder")}
            className="min-h-[220px] w-full resize-none rounded-md border border-input bg-white px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-[#CBD5E1]"
          />
          <p className="text-right text-xs text-muted-foreground">
            {t("onboardingWizard.reviewD2TextDialogCharCount", {
              count: content.trim().length,
            })}
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="button" onClick={submit} disabled={!isValid}>
            {t("onboardingWizard.reviewD2TextDialogAdd")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Review — replica del frame Figma "Registrazione - success" (4109:1129):
 * badge viola "Il tuo assistente è pronto!", titolo, "Ecco cosa ho capito"
 * (lista puntata), "Vuoi renderlo ancora più preciso?" con 3 card azione
 * (sito / fonti / dettagli), anteprima VIVA del widget con nota a mano, e
 * barra finale Personalizza / Conferma. Le fonti si aggiungono dalle card e
 * compaiono nella lista; l'anteprima risponde con ciò che è stato aggiunto.
 */
const AnalysisReview = ({
  config,
  previewConfig,
  websiteUrl,
  fromPrompt,
  sources,
  discoveredLinks = [],
  onRemoveSource,
  onAddFiles,
  onAddManual,
  fileErrors,
  addError,
  isPublishing,
  createError,
  sourcesWarning,
  onCustomize,
  onPublish,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  // Dialog aperti dalle card azione (il caricamento file apre direttamente
  // il file picker, non un dialog).
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [textDialogOpen, setTextDialogOpen] = useState(false);

  const stripProtocol = (url) =>
    (url || "").replace(/^https?:\/\//i, "").replace(/\/$/, "");

  const sourceLabel = (source) => {
    if (source.type === "sitemap")
      return t("onboardingWizard.sourceSitemapName");
    if (source.type === "url") {
      const site = stripProtocol(websiteUrl);
      return site && stripProtocol(source.url) === site
        ? t("onboardingWizard.sourceHomepageName")
        : stripProtocol(source.url);
    }
    if (source.type === "pdf") return source.filename;
    if (source.type === "faq") return source.question;
    return source.title || t("onboardingWizard.sourceTypeText");
  };

  const sourceKind = (source) => {
    if (source.type === "sitemap")
      return t("onboardingWizard.sourceTypeSitemap");
    if (source.type === "url") return t("onboardingWizard.sourceTypePage");
    if (source.type === "pdf") return t("onboardingWizard.sourceTypePdf");
    if (source.type === "faq") return t("onboardingWizard.sourceTypeFaq");
    return t("onboardingWizard.sourceTypeText");
  };

  const sourceIcon = (source) => {
    if (source.type === "sitemap") return Link2;
    if (source.type === "pdf") return FileText;
    if (source.type === "faq") return HelpCircle;
    if (source.type === "text") return FilePenLine;
    return Globe; // url/page
  };

  // Colore per tipo, per rendere la lista meno piatta (icone tutte nere).
  const sourceIconColor = (source) => {
    if (source.type === "sitemap") return "text-violet-500";
    if (source.type === "pdf") return "text-rose-500";
    if (source.type === "faq") return "text-amber-500";
    if (source.type === "text") return "text-emerald-500";
    return "text-sky-500"; // url/page
  };

  // Stato REALE di ingestione lato backend (pending/processing/ready/error):
  // le fonti entrano in coda e non sono subito pronte a rispondere, stesso
  // significato e stessi colori della sezione "Fonti" del CRM.
  const ingestStatusMeta = (status) => {
    switch (status) {
      case "ready":
        return {
          label: t("sources.statusReady"),
          icon: CheckCircle,
          className: "bg-green-50 text-green-700",
        };
      case "processing":
        return {
          label: t("sources.statusProcessing"),
          icon: RefreshCw,
          className: "bg-blue-50 text-blue-700",
          spin: true,
        };
      case "error":
        return {
          label: t("sources.statusError"),
          icon: XCircle,
          className: "bg-red-50 text-red-700",
        };
      case "pending":
      default:
        return {
          label: t("sources.statusPending"),
          icon: Clock,
          className: "bg-amber-50 text-amber-700",
        };
    }
  };

  // Solo le fonti REALMENTE capite/aggiunte: le semplici proposte ("+" da
  // aggiungere, es. la sitemap suggerita) non sono "cose che ho capito" e
  // non devono comparire in questo elenco.
  const understoodSources = sources.filter((row) => row.status !== "available");

  // Link scoperti dall'analisi ma non ancora presi come fonte (una volta
  // aggiunti dal dialog non vanno riproposti se lo si riapre).
  const addedUrls = new Set(
    understoodSources
      .filter((row) => row.source.type === "url")
      .map((row) => row.source.url)
  );
  const availableDiscoveredLinks = discoveredLinks.filter(
    (link) => !addedUrls.has(link.url)
  );

  const addTextSource = ({ title, content }) => {
    onAddManual({ type: "text", title, inline_text: content });
  };

  const actionCard = (Icon, title, subtitle, onClick, active) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-center gap-2.5 rounded-[14px] border bg-white p-3 text-left transition-colors ${
        active
          ? "border-[#020617]"
          : "border-black/10 hover:border-[#CBD5E1]"
      }`}
    >
      <Icon className="h-5 w-5 shrink-0 text-[#020617]" strokeWidth={1.5} />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-semibold tracking-[0.07px] text-[#020617]">
          {title}
        </span>
        <span className="text-xs leading-4 tracking-[0.18px] text-[#64748B]">
          {subtitle}
        </span>
      </span>
    </button>
  );

  return (
    <div className="pu-review-in -mx-4 -mb-4 min-h-[calc(100vh-4rem)] bg-[#F9FAFB] px-6 pb-4 pt-14">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fuzzy+Bubbles:wght@700&display=swap');
        @keyframes puReviewIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .pu-review-in { animation: puReviewIn .5s ease-out both; }
        @media (prefers-reduced-motion: reduce) { .pu-review-in { animation: none; } }
      `}</style>

      <div className="mx-auto flex max-w-[1060px] flex-col gap-6">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
          {/* Colonna sinistra */}
          <div className="flex min-w-0 flex-1 flex-col gap-8 lg:max-w-[640px]">
            {/* Header: badge + titolo + sottotitolo */}
            <div className="flex flex-col items-start gap-4">
              <span className="flex items-center gap-2 rounded-[20px] bg-[rgba(173,70,255,0.15)] py-1 pl-3 pr-4">
                <PartyPopper className="h-5 w-5 m-1 text-[#A855F7]" strokeWidth={2} />
                <span className="text-sm font-semibold tracking-[0.07px] text-[#A855F7]">
                  {t("onboardingWizard.reviewD2Badge")}
                </span>
              </span>
              <h1 className="text-[32px] font-semibold leading-9 text-[#020617]">
                {fromPrompt
                  ? t("onboardingWizard.reviewD2TitlePrompt")
                  : t("onboardingWizard.reviewD2TitleSite")}
              </h1>
              <p className="text-base text-[#64748B]">
                {t("onboardingWizard.reviewD2Sub1")}
              </p>
            </div>

            {/* Ecco cosa ho capito */}
            <div className="flex flex-col gap-3">
              
              <div className="rounded-[14px] border border-black/10 bg-white p-3">
                <div className="flex flex-col divide-y divide-[#F1F5F9]">
                  <IntroRow
                    name={config.name}
                    subtitle={config.subtitle}
                    welcomeMessage={config.welcomeMessage}
                  />
                  {understoodSources.map((row) => {
                    const statusMeta = row.source.ingestStatus
                      ? ingestStatusMeta(row.source.ingestStatus)
                      : null;
                    return (
                      <SourceRow
                        key={row.id}
                        icon={sourceIcon(row.source)}
                        iconClassName={sourceIconColor(row.source)}
                        title={sourceLabel(row.source)}
                        kind={sourceKind(row.source)}
                        statusBadge={
                          statusMeta && (
                            <span
                              className={`flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusMeta.className}`}
                            >
                              <statusMeta.icon
                                className={`h-3 w-3 ${statusMeta.spin ? "animate-spin" : ""}`}
                              />
                              {statusMeta.label}
                            </span>
                          )
                        }
                        action={
                          row.status === "adding" ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                          ) : (
                            <button
                              type="button"
                              onClick={() => onRemoveSource(row)}
                              aria-label={t("onboardingWizard.kbRemoveAria")}
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#94A3B8] transition-colors hover:bg-red-50 hover:text-red-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )
                        }
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Vuoi renderlo ancora più preciso? */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-4 px-4">
                <Sparkles
                  className="h-6 w-6 text-[#020617]"
                  strokeWidth={1.5}
                />
                <div className="flex flex-col gap-1">
                  <p className="text-base font-semibold text-[#020617]">
                    {t("onboardingWizard.reviewD2MoreTitle")}
                  </p>
                  <p className="text-xs leading-4 tracking-[0.18px] text-[#64748B]">
                    {t("onboardingWizard.reviewD2MoreSub")}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                {actionCard(
                  Globe,
                  t("onboardingWizard.reviewD2CardSite"),
                  t("onboardingWizard.reviewD2CardSiteSub"),
                  () => setLinkDialogOpen(true),
                  false
                )}
                {actionCard(
                  FileUp,
                  t("onboardingWizard.reviewD2CardFiles"),
                  t("onboardingWizard.reviewD2CardFilesSub"),
                  () => fileInputRef.current?.click(),
                  false
                )}
                {actionCard(
                  FilePenLine,
                  t("onboardingWizard.reviewD2CardDetails"),
                  t("onboardingWizard.reviewD2CardDetailsSub"),
                  () => setTextDialogOpen(true),
                  false
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.txt,.md"
                className="hidden"
                onChange={(e) => {
                  onAddFiles(e.target.files);
                  e.target.value = "";
                }}
              />

              <AnalyzeLinksDialog
                open={linkDialogOpen}
                onOpenChange={setLinkDialogOpen}
                links={availableDiscoveredLinks}
                onConfirm={onAddManual}
              />
              <AddTextDialog
                open={textDialogOpen}
                onOpenChange={setTextDialogOpen}
                onConfirm={addTextSource}
              />

              {fileErrors.length > 0 && (
                <div className="space-y-0.5 text-xs text-red-600">
                  {fileErrors.map((err, i) => (
                    <p key={i}>{err}</p>
                  ))}
                </div>
              )}
              {addError && <p className="text-xs text-red-600">{addError}</p>}
            </div>

            {/* Avvisi */}
            {createError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {createError}
              </div>
            )}
            {sourcesWarning && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">
                    {t("onboardingWizard.sourcesFailedTitle")}
                  </p>
                  <p className="mt-0.5 text-sm text-amber-800">{sourcesWarning}</p>
                </div>
              </div>
            )}
          </div>

          {/* Colonna destra: nota a mano + anteprima viva */}
          <aside className="w-full shrink-0 lg:w-[400px]">
            <div className="flex items-start gap-2 pl-2">
              <HandArrow />
              <p
                className="text-sm leading-snug tracking-[0.7px] text-[#64748B]"
                style={{ fontFamily: "'Fuzzy Bubbles', cursive" }}
              >
                {t("onboardingWizard.reviewD2NoteLine1")}
                <br />
                {t("onboardingWizard.reviewD2NoteLine2")}
              </p>
            </div>
            {/* Chat VERA in scala 1:1, viewport virtuale intero senza crop. */}
            <div className="relative mt-1 h-[630px] rounded-lg">
              <div className="absolute inset-0">
                <WidgetPreview config={previewConfig} desktopVH={680} />
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Barra finale: sticky, tutta larghezza, sempre visibile in fondo. */}
      <div className="sticky bottom-0 z-30 -mx-6 mt-8 border-t border-black/10 bg-[#F9FAFB]/95 px-6 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-[1060px] flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            className="min-h-10 gap-3 rounded-lg bg-[rgba(191,222,255,0.7)] pl-3 pr-4 text-sm font-semibold text-[#0F172A] hover:bg-[rgba(191,222,255,0.95)]"
            onClick={onCustomize}
            disabled={isPublishing}
          >
            <FilePenLine className="h-3.5 w-3.5" />
            {t("onboardingWizard.personalizeCta")}
          </Button>
          <Button
            type="button"
            className="min-h-10 gap-3 rounded-lg bg-[#0F172A] px-6 text-sm font-semibold text-[#F8FAFC] hover:bg-[#1E293B]"
            onClick={onPublish}
            disabled={isPublishing}
          >
            {isPublishing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            {isPublishing
              ? t("onboardingWizard.creatingBot")
              : t("onboardingWizard.confirmCta")}
            {!isPublishing && <ArrowRight className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisReview;
